import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ListChecks, Plus, Trash2, Pencil, RefreshCcw } from "lucide-react";
import { Modal, Field, FieldSelect, FieldTextarea } from "./AdminBusinesses";

const STATUSES = ["", "assigned", "in_progress", "completed", "cancelled", "failed"];
const PRIORITIES = ["low", "normal", "high"];

export default function AdminPromoterTasks() {
  const [items, setItems] = useState([]);
  const [promoters, setPromoters] = useState([]);
  const [status, setStatus] = useState("");
  const [promoterId, setPromoterId] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (promoterId) params.set("promoter_id", promoterId);
    const [tasks, ps] = await Promise.all([
      api.get(`/admin/promoter-tasks?${params}`),
      api.get(`/admin/promoters?status=active`),
    ]);
    setItems(tasks.data.items || []);
    setPromoters(ps.data.items || []);
  }, [status, promoterId]);

  useEffect(() => { load(); }, [load]);

  const onCreate = async (payload) => {
    try {
      // payload is one of:
      //  { mode: "single", form: { assigned_promoter_id, ... } }
      //  { mode: "bulk",   form: { assigned_promoter_ids: [...], assign_to_all, ... } }
      if (payload.mode === "bulk") {
        const { data } = await api.post(`/admin/promoter-tasks/bulk`, payload.form);
        toast.success(`Assigned to ${data.count} promoter${data.count === 1 ? "" : "s"}.`);
      } else {
        await api.post(`/admin/promoter-tasks`, payload.form);
        toast.success("Task assigned to promoter.");
      }
      setCreating(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed."); }
  };
  const onSave = async (patch) => {
    try {
      await api.put(`/admin/promoter-tasks/${editing.id}`, patch);
      toast.success("Task updated.");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };
  const doDelete = async () => {
    await api.delete(`/admin/promoter-tasks/${confirm.id}`);
    toast.success("Task deleted.");
    setConfirm(null);
    load();
  };

  return (
    <div data-testid="admin-promoter-tasks-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
          <ListChecks className="w-5 h-5" /> Promoter Tasks (Assigned Visits)
        </h1>
        <button data-testid="admin-task-create" onClick={() => setCreating(true)}
                className="px-3 py-1.5 text-sm rounded-lg bg-[#1B4332] text-white inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Assign new visit
        </button>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="px-2 py-1.5 text-sm rounded-lg border border-slate-300">
          {STATUSES.map((s) => <option key={s} value={s}>{s || "Any status"}</option>)}
        </select>
        <select value={promoterId} onChange={(e) => setPromoterId(e.target.value)}
                className="px-2 py-1.5 text-sm rounded-lg border border-slate-300">
          <option value="">All promoters</option>
          {promoters.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button onClick={load} className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 inline-flex items-center gap-1">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
        <span className="text-xs text-slate-500 ml-auto">{items.length} tasks</span>
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left p-2">Business</th>
              <th className="text-left p-2">Promoter</th>
              <th className="text-left p-2">Priority</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Due</th>
              <th className="text-right p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} data-testid={`task-row-${t.id}`} className="border-t border-slate-100">
                <td className="p-2 font-medium">{t.business_name}</td>
                <td className="p-2 text-slate-600">{t.assigned_promoter_name}</td>
                <td className="p-2 text-xs">{t.priority}</td>
                <td className="p-2 text-xs">{t.status}</td>
                <td className="p-2 text-slate-600">{t.due_date || "-"}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <button data-testid={`task-edit-${t.id}`} onClick={() => setEditing(t)}
                          className="text-xs px-2 py-1 rounded border border-slate-300 inline-flex items-center gap-1">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button data-testid={`task-delete-${t.id}`} onClick={() => setConfirm(t)}
                          className="ml-1 text-xs px-2 py-1 rounded border border-rose-300 text-rose-700 inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No tasks assigned yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && <TaskFormModal title="Assign new visit" promoters={promoters} onSave={onCreate}
                                    onClose={() => setCreating(false)} testId="task-create-modal" />}
      {editing && <TaskFormModal title="Edit task" item={editing} promoters={promoters} onSave={onSave}
                                   onClose={() => setEditing(null)} testId="task-edit-modal" />}
      {confirm && (
        <Modal title="Delete this task?" onClose={() => setConfirm(null)} testId="confirm-delete-task">
          <p className="text-sm text-slate-700">This permanently removes the task. The promoter will no longer see it.</p>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setConfirm(null)} className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
            <button data-testid="confirm-delete-task-yes" onClick={doDelete} className="px-3 py-1.5 text-sm rounded bg-rose-600 text-white">Yes, delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function TaskFormModal({ title, item, promoters, onSave, onClose, testId }) {
  const isEdit = !!item;
  // Selection mode: "single" | "multi" | "all": only used in CREATE mode.
  const [mode, setMode] = useState("single");
  const [selectedIds, setSelectedIds] = useState(item ? [item.assigned_promoter_id] : []);
  const [form, setForm] = useState({
    assigned_promoter_id: item?.assigned_promoter_id || (promoters[0]?.id || ""),
    business_name: item?.business_name || "",
    business_address: item?.business_address || "",
    contact_name: item?.contact_name || "",
    contact_info: item?.contact_info || "",
    reason: item?.reason || "",
    instructions: item?.instructions || "",
    priority: item?.priority || "normal",
    due_date: item?.due_date || "",
    admin_notes: item?.admin_notes || "",
    status: item?.status || "assigned",
  });

  const toggleId = (id) =>
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const submit = () => {
    const base = { ...form };
    if (!base.due_date) delete base.due_date;
    if (isEdit) {
      // Edit always single-target: keep the existing endpoint.
      onSave(base);
      return;
    }
    // CREATE: branch on mode.
    if (mode === "all") {
      const { assigned_promoter_id, status, ...rest } = base;
      onSave({ mode: "bulk", form: { ...rest, assign_to_all: true } });
    } else if (mode === "multi") {
      if (selectedIds.length === 0) return;
      const { assigned_promoter_id, status, ...rest } = base;
      onSave({ mode: "bulk", form: { ...rest, assigned_promoter_ids: selectedIds } });
    } else {
      // single
      const { status, ...rest } = base;
      onSave({ mode: "single", form: rest });
    }
  };

  // Map promoter id -> display name for the single dropdown.
  const promoterOptions = promoters.map((p) => ({ value: p.id, label: `${p.name}: ${p.email}` }));

  return (
    <Modal title={title} onClose={onClose} testId={testId}>
      <div className="space-y-3 text-sm max-h-[60vh] overflow-y-auto pr-1">
        {/* Assignment selector: only in CREATE mode */}
        {!isEdit && (
          <div data-testid="task-assignment-selector"
                className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs font-semibold text-slate-700 mb-2">Assign to</div>
            <div className="flex flex-wrap gap-2 text-xs">
              <ModePill testId="task-mode-single" active={mode === "single"}
                          onClick={() => setMode("single")}>One promoter</ModePill>
              <ModePill testId="task-mode-multi"  active={mode === "multi"}
                          onClick={() => setMode("multi")}>Multiple promoters</ModePill>
              <ModePill testId="task-mode-all"    active={mode === "all"}
                          onClick={() => setMode("all")}>All active promoters ({promoters.length})</ModePill>
            </div>
            {mode === "single" && (
              <div className="mt-3">
                <select data-testid="task-single-promoter" value={form.assigned_promoter_id}
                          onChange={(e) => setForm({ ...form, assigned_promoter_id: e.target.value })}
                          className="w-full px-3 py-1.5 rounded border border-slate-300">
                  {promoterOptions.map((o) =>
                    <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            )}
            {mode === "multi" && (
              <div className="mt-3 grid sm:grid-cols-2 gap-1 max-h-40 overflow-y-auto"
                    data-testid="task-multi-list">
                {promoters.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-xs px-2 py-1 rounded hover:bg-white">
                    <input type="checkbox" data-testid={`task-multi-${p.id}`}
                            checked={selectedIds.includes(p.id)}
                            onChange={() => toggleId(p.id)} />
                    <span className="truncate"><strong>{p.name}</strong> · {p.email}</span>
                  </label>
                ))}
                {promoters.length === 0 && <div className="text-xs text-slate-500">No active promoters: register one first.</div>}
              </div>
            )}
            {mode === "all" && (
              <div className="mt-2 text-xs text-slate-600">
                One task will be created for each of the <strong>{promoters.length}</strong> active promoters.
              </div>
            )}
          </div>
        )}

        <Field label="Business name" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} />
        <Field label="Business address" value={form.business_address} onChange={(v) => setForm({ ...form, business_address: v })} />
        <Field label="Contact person" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} />
        <Field label="Contact info" value={form.contact_info} onChange={(v) => setForm({ ...form, contact_info: v })} />
        <Field label="Reason for visit" value={form.reason} onChange={(v) => setForm({ ...form, reason: v })} />
        <FieldTextarea label="Instructions" value={form.instructions} onChange={(v) => setForm({ ...form, instructions: v })} />
        <FieldSelect label="Priority" value={form.priority} options={PRIORITIES}
                       onChange={(v) => setForm({ ...form, priority: v })} />
        <Field label="Due date (YYYY-MM-DD)" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} />
        {isEdit && <FieldSelect label="Status"
                                  value={form.status} options={["assigned","in_progress","completed","cancelled","failed"]}
                                  onChange={(v) => setForm({ ...form, status: v })} />}
        <FieldTextarea label="Admin internal notes" value={form.admin_notes}
                         onChange={(v) => setForm({ ...form, admin_notes: v })} />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
        <button data-testid="task-form-submit" onClick={submit}
                disabled={!isEdit && mode === "multi" && selectedIds.length === 0}
                className="px-3 py-1.5 text-sm rounded bg-[#1B4332] text-white disabled:opacity-50">
          {isEdit ? "Save" : (mode === "single" ? "Assign" : `Assign to ${mode === "all" ? promoters.length : selectedIds.length}`)}
        </button>
      </div>
    </Modal>
  );
}

function ModePill({ active, onClick, children, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
              className={`px-2 py-1 rounded-full border text-[11px] uppercase tracking-wide font-semibold ${
                active ? "bg-[#1B4332] text-white border-[#1B4332]"
                        : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
              }`}>
      {children}
    </button>
  );
}
